import { debounce } from 'lodash';
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import io, { type Socket } from 'socket.io-client';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketEventName, SocketKey } from '@/Config/api/socketKey';
import { getScenarioId, getToken, refreshToken } from '@/Config/auth/authStore';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

const SOCKET_CONNECT_REFUSAL_TOKEN_EXPIRED = 'token_expired';
const MAX_TOKEN_EXPIRED_REFRESH_ATTEMPTS = 3;

export interface SocketContextType {
  socket: Socket;
}

export const SocketContext = createContext<SocketContextType | null>(null);

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === null) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface OpenInboxStatus {
  ai_assistant_conversations_count: number;
  unassigned_conversations_count: number;
}

interface ResolvedInboxStatus {
  ai_assistant_conversations_count: number;
  human_conversations_count: number;
}

export interface InboxStatus {
  total_conversations_count: number;
  open_conversations_counts: OpenInboxStatus;
  resolved_conversations_counts: ResolvedInboxStatus;
}

interface InboxStatusUpdatedSocketEvent {
  inbox_status: InboxStatus;
}

export interface UserInboxStatus {
  user_id: number;
  my_conversations_count: number;
  other_conversations_count: number;
}

interface UserInboxStatusUpdatedSocketEvent {
  user_inbox_status: UserInboxStatus;
}

interface ConversationEventCreatedSocketEvent {
  conversation_id: string;
  event: Event;
}

interface ConversationCreatedSocketEvent {
  conversation: ConversationDto;
}

interface PayloadBySocketEvent {
  [SocketKey.inboxStatus.general]: InboxStatusUpdatedSocketEvent;
  [SocketKey.inboxStatus.user]: UserInboxStatusUpdatedSocketEvent;
  [SocketKey.conversation.eventCreated]: ConversationEventCreatedSocketEvent;
  [SocketKey.conversation.conversationCreated]: ConversationCreatedSocketEvent;
}

export type ServerToClientEvents = {
  [K in SocketEventName]?: (data: PayloadBySocketEvent[K & keyof PayloadBySocketEvent]) => void;
};

export function SocketProvider({ children }: PropsWithChildren): JSX.Element {
  const { user } = useUser();
  const { apiServerUrl } = useBootstrapConfig();

  const scenario_id = getScenarioId();
  const url = `https://${apiServerUrl}/admin_dashboard`;

  const [socket] = useState<Socket<ServerToClientEvents>>(() =>
    io(url, {
      auth: (cb) => {
        cb({ token: getToken() });
      },
      query: { scenario_id },
      transports: ['websocket', 'polling'],
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 10000,
    }),
  );
  const value = {
    socket,
  };

  useEffect(() => {
    socket.connect();

    let tokenExpiredAttempts = 0;

    // Debounced so a reconnect storm (multiple tabs, flapping network) coalesces
    // into a single pair of refetches instead of one per connect event.
    const refetchInboxStatus = debounce(() => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.inboxStatus.general] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.inboxStatus.my] });
    }, 100);
    const handleConnect = () => {
      tokenExpiredAttempts = 0;
      refetchInboxStatus();
    };
    socket.on('connect', handleConnect);

    // Background tabs freeze the authStore refresh timer, so the socket can
    // reconnect with an already-expired token and the server rejects. Force a
    // refresh here, then let socket.io retry. Bail after N attempts so a server
    // that keeps rejecting fresh tokens (clock skew, signer replica lag) doesn't
    // loop forever.
    const handleConnectError = (error: Error) => {
      if (error.message !== SOCKET_CONNECT_REFUSAL_TOKEN_EXPIRED) return;
      if (tokenExpiredAttempts >= MAX_TOKEN_EXPIRED_REFRESH_ATTEMPTS) return;
      tokenExpiredAttempts += 1;
      void refreshToken().then((newToken) => {
        if (newToken && !socket.connected) socket.connect();
      });
    };
    socket.on('connect_error', handleConnectError);

    // Recover the connection when the tab/network comes back. socket.io
    // gives up after its retry budget; without these triggers a long-lived
    // tab can sit dead until the user manually reloads.
    const handleRecovery = () => {
      if (!socket.connected) socket.connect();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (socket.connected) return;
      // Proactively refresh before reconnecting so we don't burn a
      // retry attempt with a token we already know might be expired.
      void refreshToken().finally(handleRecovery);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleRecovery);
    window.addEventListener('pageshow', handleRecovery);
    window.addEventListener('online', handleRecovery);

    return () => {
      refetchInboxStatus.cancel();
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleRecovery);
      window.removeEventListener('pageshow', handleRecovery);
      window.removeEventListener('online', handleRecovery);
      socket.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    const handleInboxStatus = ({ inbox_status: inboxStatus }: InboxStatusUpdatedSocketEvent) => {
      queryClient.setQueryData([QueryKey.inboxStatus.general], inboxStatus);
    };
    socket.on(SocketKey.inboxStatus.general, handleInboxStatus);
    return () => {
      socket.off(SocketKey.inboxStatus.general, handleInboxStatus);
    };
  }, [socket]);

  useEffect(() => {
    const handleUserInboxStatus = ({
      user_inbox_status: userInboxStatus,
    }: UserInboxStatusUpdatedSocketEvent) => {
      if (user?.id !== userInboxStatus.user_id) return;
      queryClient.setQueryData([QueryKey.inboxStatus.my], userInboxStatus);
    };
    socket.on(SocketKey.inboxStatus.user, handleUserInboxStatus);
    return () => {
      socket.off(SocketKey.inboxStatus.user, handleUserInboxStatus);
    };
  }, [socket, user?.id]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
